(() => {
  'use strict';

  const config = window.AWAAKII_THEME || {};
  const routes = config.routes || {};
  const moneyFormat = config.moneyFormat || '${{amount}}';

  const formatMoney = (cents, format = moneyFormat) => {
    const value = Number(cents || 0);
    const token = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [null, 'amount'])[1];
    const formats = {
      amount: [2, '.', ','],
      amount_no_decimals: [0, '.', ','],
      amount_with_comma_separator: [2, ',', '.'],
      amount_no_decimals_with_comma_separator: [0, ',', '.']
    };
    const [precision, decimal, thousands] = formats[token] || formats.amount;
    const fixed = (value / 100).toFixed(precision);
    let [whole, fraction] = fixed.split('.');
    whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const formatted = precision ? whole + decimal + fraction : whole;
    return format.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  }[char]));

  const handleize = (value = '') => String(value).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const updateGalleryCounter = (gallery, position) => {
    const counter = gallery?.querySelector('[data-gallery-current]');
    if (counter) counter.textContent = String(Number(position) || 1).padStart(2, '0');
  };

  const finishPresentation = {
    obsidian: { name: 'Obsidian', glow: '98, 155, 178' },
    graphite: { name: 'Graphite', glow: '135, 151, 163' },
    'rose-gold': { name: 'Rose Gold', glow: '198, 132, 122' },
    gold: { name: 'Gold', glow: '196, 156, 86' },
    silver: { name: 'Silver', glow: '196, 215, 224' }
  };

  const activateGalleryMedia = (gallery, mediaId, position = 1, finishName = '') => {
    if (!gallery || !mediaId) return;
    const mediaItems = [...gallery.querySelectorAll('[data-media-id]')];
    const selected = mediaItems.find((media) => String(media.dataset.mediaId) === String(mediaId));
    if (!selected) return;
    const reduceMotion = matchMedia('(prefers-reduced-motion:reduce)').matches;
    gallery.dataset.activeMedia = String(mediaId);

    mediaItems.forEach((media) => {
      window.clearTimeout(media._awaakiiHideTimer);
      if (media === selected) {
        media.hidden = false;
        media.classList.remove('is-leaving');
        if (media.classList.contains('is-active')) return;
        media.classList.remove('is-active');
        if (reduceMotion) {
          media.classList.remove('is-entering');
          media.classList.add('is-active');
          return;
        }
        media.classList.add('is-entering');
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (gallery.dataset.activeMedia !== String(mediaId)) return;
          media.classList.remove('is-entering');
          media.classList.add('is-active');
        }));
        return;
      }

      media.classList.remove('is-entering', 'is-active');
      if (media.hidden || reduceMotion) {
        media.classList.remove('is-leaving');
        media.hidden = true;
        return;
      }
      media.classList.add('is-leaving');
      media._awaakiiHideTimer = window.setTimeout(() => {
        media.classList.remove('is-leaving');
        media.hidden = true;
      }, 620);
    });

    gallery.querySelectorAll('[data-media-target]').forEach((button) => {
      button.setAttribute('aria-pressed', String(String(button.dataset.mediaTarget) === String(mediaId)));
    });
    updateGalleryCounter(gallery, position);

    const finishHandle = handleize(finishName) || selected.dataset.fallbackFinish || '';
    const finish = finishPresentation[finishHandle];
    if (finish) {
      gallery.style.setProperty('--pdp-finish-glow', finish.glow);
      const label = gallery.querySelector('[data-gallery-finish-name]');
      if (label) label.textContent = `${finish.name} finish`;
      const stage = gallery.querySelector('[data-gallery-stage]');
      if (stage && !reduceMotion) {
        stage.classList.remove('is-finish-changing');
        void stage.offsetWidth;
        stage.classList.add('is-finish-changing');
      }
    }
  };

  class CartDrawer {
    constructor(element) {
      this.element = element;
      this.dialog = element.querySelector('.cart-drawer');
      this.items = element.querySelector('[data-cart-items]');
      this.footer = element.querySelector('[data-cart-footer]');
      this.subtotal = element.querySelector('[data-cart-subtotal]');
      this.status = element.querySelector('[data-cart-status]');
      element.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      document.querySelectorAll('[data-cart-open]').forEach((button) => button.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      }));
      element.addEventListener('change', (event) => {
        const input = event.target.closest('[data-cart-quantity]');
        if (input) this.change(input.closest('[data-line-key]').dataset.lineKey, Number(input.value));
      });
      element.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (remove) this.change(remove.closest('[data-line-key]').dataset.lineKey, 0);
      });
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !this.element.hidden) this.close(); });
    }

    open() {
      this.element.hidden = false;
      requestAnimationFrame(() => {
        this.element.classList.add('is-open');
        document.body.classList.add('cart-open');
        this.dialog?.focus();
      });
    }

    close() {
      this.element.classList.remove('is-open');
      document.body.classList.remove('cart-open');
      window.setTimeout(() => { this.element.hidden = true; }, 420);
    }

    async fetchCart() {
      const response = await fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart could not be loaded.');
      return response.json();
    }

    async refresh(open = false) {
      const cart = await this.fetchCart();
      this.render(cart);
      if (open) this.open();
      return cart;
    }

    async change(key, quantity) {
      this.setStatus('Updating cart…');
      const response = await fetch(routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Cart could not be updated.');
      this.render(cart);
      this.setStatus('Cart updated.');
    }

    setStatus(message) {
      if (!this.status) return;
      this.status.textContent = message;
      window.clearTimeout(this.statusTimer);
      this.statusTimer = window.setTimeout(() => { this.status.textContent = ''; }, 1600);
    }

    render(cart) {
      document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
      if (!cart.items.length) {
        this.items.innerHTML = `<div class="cart-empty"><p>Your cart is currently empty.</p><a class="button button-ghost" href="${escapeHtml(routes.allProducts || '/collections/all')}">Explore products</a></div>`;
        this.footer.hidden = true;
        return;
      }
      this.items.innerHTML = cart.items.map((item) => {
        const image = item.featured_image?.url || item.image || '';
        const options = (item.options_with_values || []).map((option) => `<small>${escapeHtml(option.name)}: ${escapeHtml(option.value)}</small>`).join('');
        const resizedImage = image ? image + (image.includes('?') ? '&' : '?') + 'width=240' : '';
        return `<article class="cart-item" data-line-key="${escapeHtml(item.key)}">
          <a class="cart-item-image" href="${escapeHtml(item.url)}">${image ? `<img src="${escapeHtml(resizedImage)}" alt="${escapeHtml(item.product_title)}" loading="lazy">` : ''}</a>
          <div><a class="cart-item-title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>${options}<strong>${formatMoney(item.final_line_price)}</strong>
          <div class="cart-item-actions"><label><span class="sr-only">Quantity</span><input type="number" min="0" value="${item.quantity}" data-cart-quantity></label><button type="button" data-cart-remove>Remove</button></div></div>
        </article>`;
      }).join('');
      this.footer.hidden = false;
      this.subtotal.textContent = formatMoney(cart.total_price);
    }
  }

  class ProductForm {
    constructor(root, cartDrawer) {
      if (root.dataset.initialized === 'true') return;
      root.dataset.initialized = 'true';
      this.root = root;
      this.cartDrawer = cartDrawer;
      this.form = root.querySelector('[data-product-form]');
      this.json = root.querySelector('[data-product-json]');
      if (!this.form || !this.json) return;
      const productJson = this.json.content?.textContent || this.json.textContent;
      this.product = JSON.parse(productJson);
      this.idInput = root.querySelector('[data-variant-id]');
      this.addButton = root.querySelector('[data-product-add]');
      this.buyNowButton = root.querySelector('[data-product-buy-now]');
      this.addLabel = root.querySelector('[data-add-label]');
      this.price = root.querySelector('[data-variant-price]');
      this.compare = root.querySelector('[data-variant-compare]');
      this.status = root.querySelector('[data-variant-status]');
      this.sku = root.querySelector('[data-variant-sku]');
      this.error = root.querySelector('[data-product-error]');
      this.optionGroups = [...root.querySelectorAll('[data-option-index]')];
      root.querySelectorAll('[data-option-value]').forEach((button) => button.addEventListener('click', () => this.selectOption(button)));
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.update();
    }

    get selections() {
      return this.optionGroups.map((group) => group.querySelector('[data-option-value].is-selected')?.dataset.optionValue || '');
    }

    selectOption(button) {
      const group = button.closest('[data-option-index]');
      group.querySelectorAll('[data-option-value]').forEach((item) => {
        const selected = item === button;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', String(selected));
      });
      const label = group.querySelector('[data-selected-option]');
      if (label) label.textContent = button.dataset.optionValue;
      this.update();
    }

    update() {
      const selections = this.selections;
      const variant = this.product.variants.find((item) => item.options.every((option, index) => option === selections[index]));
      this.variant = variant;
      this.updateOptionAvailability(selections);
      if (!variant) {
        this.setUnavailable('Unavailable');
        return;
      }
      this.idInput.value = variant.id;
      this.price.textContent = formatMoney(variant.price);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.compare.textContent = formatMoney(variant.compare_at_price);
        this.compare.classList.remove('is-hidden');
      } else {
        this.compare.textContent = '';
        this.compare.classList.add('is-hidden');
      }
      this.sku.textContent = variant.sku || '—';
      this.status.textContent = variant.available ? 'Available' : 'Sold out';
      this.addButton.disabled = !variant.available;
      if (this.buyNowButton) this.buyNowButton.disabled = !variant.available;
      this.addLabel.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      this.updateMedia(variant);
      if (this.root.dataset.updateUrl === 'true' && history.replaceState) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
      const finishIndex = (this.product.options || []).findIndex((option) => /finish|colou?r/i.test(typeof option === 'string' ? option : option.name || ''));
      this.root.dispatchEvent(new CustomEvent('awaakii:variant-change', {
        bubbles: true,
        detail: { variant, finish: finishIndex >= 0 ? variant.options[finishIndex] : '' }
      }));
    }

    setUnavailable(label) {
      this.status.textContent = label;
      this.addButton.disabled = true;
      if (this.buyNowButton) this.buyNowButton.disabled = true;
      this.addLabel.textContent = label;
      this.sku.textContent = '—';
    }

    updateOptionAvailability(selections) {
      this.optionGroups.forEach((group, optionIndex) => {
        group.querySelectorAll('[data-option-value]').forEach((button) => {
          const test = [...selections];
          test[optionIndex] = button.dataset.optionValue;
          const possible = this.product.variants.some((variant) => variant.available && variant.options.every((value, index) => !test[index] || value === test[index]));
          button.classList.toggle('is-unavailable', !possible);
          button.setAttribute('aria-disabled', String(!possible));
        });
      });
    }

    updateMedia(variant) {
      const gallery = this.root.closest('.product-layout')?.querySelector('[data-product-gallery]');
      if (!gallery) return;
      let mediaId = variant.featured_media?.id || variant.featured_image?.id;
      const finishIndex = (this.product.options || []).findIndex((option) => /finish|colou?r/i.test(typeof option === 'string' ? option : option.name || ''));
      const finishName = finishIndex >= 0 ? variant.options[finishIndex] : '';
      if (!mediaId) {
        const finish = handleize(finishName);
        mediaId = gallery.querySelector(`[data-fallback-finish="${finish}"]`)?.dataset.mediaId;
      }
      if (!mediaId) return;
      const activePosition = gallery.querySelector(`[data-media-id="${mediaId}"]`)?.dataset.mediaPosition || 1;
      activateGalleryMedia(gallery, mediaId, activePosition, finishName);
    }

    async submit(event) {
      if (event.submitter && event.submitter.name === 'checkout') return;
      event.preventDefault();
      if (!this.variant?.available) return;
      this.error.hidden = true;
      this.addButton.disabled = true;
      this.addLabel.textContent = 'Adding…';
      try {
        const response = await fetch(routes.cartAdd + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'This item could not be added.');
        await this.cartDrawer?.refresh(true);
        this.addLabel.textContent = 'Added';
      } catch (error) {
        this.error.textContent = error.message;
        this.error.hidden = false;
        this.addLabel.textContent = 'Try again';
      } finally {
        window.setTimeout(() => {
          this.addButton.disabled = !this.variant?.available;
          this.addLabel.textContent = this.variant?.available ? 'Add to Cart' : 'Sold Out';
        }, 1200);
      }
    }
  }

  const initGallery = (scope = document) => {
    scope.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.initialized) return;
      gallery.dataset.initialized = 'true';
      gallery.querySelectorAll('[data-media-target]').forEach((button) => button.addEventListener('click', () => {
        const media = gallery.querySelector(`[data-media-id="${button.dataset.mediaTarget}"]`);
        const finish = media?.dataset.fallbackFinish || '';
        if (finish) {
          const option = [...(gallery.closest('.product-layout')?.querySelectorAll('[data-option-value]') || [])].find((item) => handleize(item.dataset.optionValue) === finish);
          if (option && !option.classList.contains('is-selected')) {
            option.click();
            return;
          }
        }
        activateGalleryMedia(gallery, button.dataset.mediaTarget, button.dataset.mediaPosition || 1, finish);
      }));
      const stage = gallery.querySelector('[data-gallery-stage]');
      if (stage && matchMedia('(pointer:fine)').matches && !matchMedia('(prefers-reduced-motion:reduce)').matches) {
        stage.addEventListener('pointermove', (event) => {
          const rect = stage.getBoundingClientRect();
          stage.style.setProperty('--pdp-gallery-x', (((event.clientX - rect.left) / rect.width) - .5) * 16);
          stage.style.setProperty('--pdp-gallery-y', (((event.clientY - rect.top) / rect.height) - .5) * 16);
        });
        stage.addEventListener('pointerleave', () => {
          stage.style.setProperty('--pdp-gallery-x', 0);
          stage.style.setProperty('--pdp-gallery-y', 0);
        });
      }
    });
  };

  const initCapabilities = (scope = document) => {
    scope.querySelectorAll('[data-pdp-capabilities]').forEach((section) => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      const tabs = [...section.querySelectorAll('[data-pdp-capability-tab]')];
      const panels = [...section.querySelectorAll('[data-pdp-capability-panel]')];
      const activate = (tab, focus = false) => {
        const id = tab.dataset.pdpCapabilityTab;
        tabs.forEach((item) => item.setAttribute('aria-selected', String(item === tab)));
        panels.forEach((panel) => {
          const selected = panel.dataset.pdpCapabilityPanel === id;
          if (selected) {
            panel.hidden = false;
            panel.classList.remove('is-active');
            requestAnimationFrame(() => panel.classList.add('is-active'));
          } else {
            panel.classList.remove('is-active');
            panel.hidden = true;
          }
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          const direction = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1;
          activate(tabs[(index + direction + tabs.length) % tabs.length], true);
        });
      });
    });
  };

  const initFinishes = (scope = document) => {
    scope.querySelectorAll('[data-pdp-finishes]').forEach((section) => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      const tabs = [...section.querySelectorAll('[data-pdp-finish]')];
      const images = [...section.querySelectorAll('[data-pdp-finish-image]')];
      const name = section.querySelector('[data-pdp-finish-name]');
      const copy = section.querySelector('[data-pdp-finish-copy]');
      const index = section.querySelector('[data-pdp-finish-index]');
      const activate = (finish, syncVariant = false) => {
        const tab = tabs.find((item) => item.dataset.pdpFinish === handleize(finish));
        if (!tab) return;
        tabs.forEach((item) => item.setAttribute('aria-selected', String(item === tab)));
        images.forEach((image) => {
          const selected = image.dataset.pdpFinishImage === tab.dataset.pdpFinish;
          if (selected) {
            image.hidden = false;
            requestAnimationFrame(() => image.classList.add('is-active'));
          } else {
            image.classList.remove('is-active');
            window.setTimeout(() => { if (!image.classList.contains('is-active')) image.hidden = true; }, 460);
          }
        });
        if (name) name.textContent = tab.dataset.finishName;
        if (copy) copy.textContent = tab.dataset.finishCopy;
        if (index) index.textContent = tab.dataset.finishIndex;
        if (syncVariant) {
          const option = [...document.querySelectorAll('[data-product-root] [data-option-value]')].find((button) => handleize(button.dataset.optionValue) === tab.dataset.pdpFinish);
          option?.click();
        }
      };
      tabs.forEach((tab) => tab.addEventListener('click', () => activate(tab.dataset.pdpFinish, true)));
      document.addEventListener('awaakii:variant-change', (event) => { if (event.detail?.finish) activate(event.detail.finish); });
      const selectedFinish = [...document.querySelectorAll('[data-product-root] [data-option-index]')].find((group) => /finish|colou?r/i.test(group.querySelector('legend')?.textContent || ''))?.querySelector('[data-option-value].is-selected')?.dataset.optionValue;
      if (selectedFinish) activate(selectedFinish);
    });
  };

  const initStickyPurchase = (scope = document) => {
    scope.querySelectorAll('[data-pdp-main]').forEach((section) => {
      if (section.dataset.stickyInitialized) return;
      section.dataset.stickyInitialized = 'true';
      const shell = section.closest('.main-product-wrapper') || section.parentElement;
      const sticky = shell?.querySelector('[data-pdp-sticky]');
      const root = section.querySelector('[data-product-root]');
      const form = root?.querySelector('[data-product-form]');
      const add = root?.querySelector('[data-product-add]');
      const stickyAdd = sticky?.querySelector('[data-pdp-sticky-add]');
      const buyRow = root?.querySelector('.product-buy-row');
      if (!sticky || !root || !form || !add || !stickyAdd || !buyRow) return;
      stickyAdd.addEventListener('click', () => form.requestSubmit(add));
      root.addEventListener('awaakii:variant-change', (event) => {
        const variant = event.detail.variant;
        sticky.querySelector('[data-pdp-sticky-price]').textContent = formatMoney(variant.price);
        sticky.querySelector('[data-pdp-sticky-options]').textContent = variant.title;
        sticky.querySelector('[data-pdp-sticky-label]').textContent = variant.available ? 'Add to Cart' : 'Sold Out';
        stickyAdd.disabled = !variant.available;
      });
      let ticking = false;
      const update = () => {
        const visible = buyRow.getBoundingClientRect().bottom < 86;
        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', String(!visible));
        ticking = false;
      };
      addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      }, { passive: true });
      update();
    });
  };

  const initSizeGuides = (scope = document) => {
    scope.querySelectorAll('[data-product-root]').forEach((root) => {
      const modal = root.querySelector('[data-size-guide]');
      if (!modal || modal.dataset.initialized) return;
      modal.dataset.initialized = 'true';
      const open = () => { modal.hidden = false; requestAnimationFrame(() => modal.classList.add('is-open')); };
      const close = () => { modal.classList.remove('is-open'); setTimeout(() => { modal.hidden = true; }, 300); };
      root.querySelectorAll('[data-size-guide-open]').forEach((button) => button.addEventListener('click', open));
      modal.querySelectorAll('[data-size-guide-close]').forEach((button) => button.addEventListener('click', close));
    });
  };

  const drawerElement = document.querySelector('[data-cart-drawer]');
  const cartDrawer = drawerElement ? new CartDrawer(drawerElement) : null;
  const init = (scope = document) => {
    scope.querySelectorAll('[data-product-root]').forEach((root) => new ProductForm(root, cartDrawer));
    initGallery(scope);
    initSizeGuides(scope);
    initCapabilities(scope);
    initFinishes(scope);
    initStickyPurchase(scope);
  };
  init();

  document.querySelectorAll('[data-announcement-close]').forEach((button) => button.addEventListener('click', () => {
    const bar = button.closest('[data-announcement-bar]');
    bar?.setAttribute('hidden', '');
    document.body.classList.add('announcement-hidden');
  }));
  document.querySelectorAll('[data-header-cart-link]').forEach((link) => {
    link.addEventListener('click', (event) => {
      if (!cartDrawer) return;
      event.preventDefault();
      cartDrawer.open();
    });
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
